import { toRefs } from 'vue'
import { type Core, type DMApplicationData, type DMStateData } from '@nsftx/systems-sdk'
import { logService } from '@nsftx/seven-client-retail-utils'
import DMConfigError from '#src/classes/DMConfigError'
import DMCoreError from '#src/classes/DMCoreError'
import errorParser from '#src/common/error-handling/errorParser'
import type DMGetDataResponse from '#src/interfaces/DMGetDataResponse'
import { useDeviceManagementStore } from '#src/stores/deviceManagementStore'
import type DMApplicationConfigResponse from '#src/types/DMApplicationConfigResponse'
import { getAppConfigData } from './appDataApiService'
import { useWorkingTimeStore } from '#src/main'
import { getApplication } from './deviceManagementDisplayService'

const LOG_PREFIX = '[appDataService]'
const GET_APP_DATA_TIMEOUT = 10

const getApplicationConfig = (appRunUuid: string) =>
  new Promise<DMApplicationConfigResponse>((resolve, reject) => {
    getApplication(appRunUuid)
      .then((data: string) => {
        const parsedData: DMApplicationData = JSON.parse(data)

        if (!parsedData?.config_url) {
          const message = 'Config url is empty'
          const code = 'DM_DISPLAY_DATA_CONFIG_URL_EMPTY'
          logService.error(`${LOG_PREFIX} ${message}`, {
            code,
            data
          })
          reject(new DMConfigError(message, code))
          return
        }

        getAppConfigData(parsedData.config_url)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            const parsedError = errorParser.parseUpstream(err)
            const code = 'DM_CONFIG_DATA_FETCH_FAILED'
            logService.error(`${LOG_PREFIX} Failed to fetch application config data`, {
              code,
              ...parsedError
            })
            reject(
              new DMConfigError('Failed to fetch application config data', code, {
                cause: err
              })
            )
          })
      })
      .catch((error) => {
        const message = 'Failed to fetch application config url'
        const code = 'DM_CONFIG_URL_FETCH_FAILED'
        logService.error(`${LOG_PREFIX} ${message}`, {
          code,
          ...errorParser.parseUpstream(error)
        })
        reject(
          new DMConfigError(message, code, {
            cause: error
          })
        )
      })
  })

const mapDMDataResponse = (
  appConfigData: DMApplicationConfigResponse,
  deviceStateData: DMStateData
): DMGetDataResponse => ({
  device: {
    name: deviceStateData.device.name,
    uuid: deviceStateData.device.uuid,
    deviceId: deviceStateData.device.device_id
  },
  betshop: {
    betshopId: deviceStateData.location.betshop_id,
    companyId: deviceStateData.space.company_id,
    uuid: deviceStateData.location.uuid,
    name: deviceStateData.location.name,
    companyName: deviceStateData.space.name,
    companyUuid: deviceStateData.space.uuid
  },
  appConfig: {
    data: {
      appConfig: {
        products: appConfigData.app_config.products,
        terminalAccount: {
          uuid: appConfigData.app_config.terminal_account.uuid,
          password: appConfigData.app_config.terminal_account.password
        }
      },
      sevenUrl: appConfigData.seven_url,
      sevenSocketUrl: appConfigData.seven_socket_url,
      supportedLanguages: appConfigData.space_supported_languages,
      defaultLanguage: appConfigData.space_default_language
    }
  }
})

const getAppData = (core: Core, appRunUuid: string) =>
  new Promise<void>((resolve, reject) => {
    const { isDmReady, appData } = toRefs(useDeviceManagementStore())

    if (!core || !isDmReady.value) {
      reject(new DMCoreError('Core is not initialized', 'DM_CORE_NOT_INITIALIZED'))
      return
    }
    // if we don't receive data from DM in {GET_APP_DATA_TIMEOUT} seconds, reject the request
    const getDataTimer = setTimeout(() => {
      const message = 'Failed to fetch app data from DM runtime.'
      const code = 'DM_APP_DATA_FETCH_FAILED'

      logService.error(`${LOG_PREFIX} ${message} (after ${GET_APP_DATA_TIMEOUT} seconds).`, {
        code
      })

      const error = new DMConfigError(message, code)
      reject(error)
    }, GET_APP_DATA_TIMEOUT * 1000)

    return Promise.all([getApplicationConfig(appRunUuid), core.deviceState()])
      .then((data) => {
        clearTimeout(getDataTimer)
        appData.value = mapDMDataResponse(...data)

        const workingTimeStore = useWorkingTimeStore()
        workingTimeStore.setWorkingTimeData(data[1].location.working_time)

        logService.info(`${LOG_PREFIX} Device data successfully fetched`, {
          // manually add device data to log, not yet picked up by GRAPE plugin
          app_run_uuid: appRunUuid,
          tenant_id: appData.value.betshop.companyUuid,
          tenant_name: appData.value.betshop.companyName,
          device_id: appData.value.device.uuid,
          betshop_id: appData.value.betshop.uuid,
          code: 'DM_GET_APP_DATA_SUCCESS'
        })

        resolve()
      })
      .catch((error) => {
        clearTimeout(getDataTimer)
        if (error instanceof DMConfigError) {
          reject(error)
        } else {
          const { upstream_code, upstream_message } = errorParser.parseUpstream(error)
          const code = 'DM_GET_APP_DATA_ERROR'
          const configError = new DMConfigError(upstream_message, code)

          logService.error(`${LOG_PREFIX} Failed to get app data`, {
            upstream_message,
            upstream_code,
            code
          })
          reject(configError)
        }
      })
  })

export { getAppData, getApplicationConfig, mapDMDataResponse }
